"use client";

import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Typography,
  styled,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import useGenerateCandidateSummary from "@lib/candidates/useGenerateCandidateSummary";
import SummaryLoading from "./SummaryLoading";

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const Section = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  fontSize: "0.75rem",
  fontWeight: 700,
  color: theme.palette.text.secondary,
}));

const Count = styled(Box)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.disabled,
}));

const Bullets = styled("ul")(({ theme }) => ({
  margin: 0,
  paddingLeft: theme.spacing(2.5),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.75),
}));

const ChipRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(6, 2),
  textAlign: "center",
}));

function relativeTime(iso) {
  if (!iso) {
    return "";
  }
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return "";
  }
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) {
    return "Updated today";
  }
  if (days === 1) {
    return "Updated 1d ago";
  }
  return `Updated ${days}d ago`;
}

function citationUrl(c) {
  if (typeof c === "string") {
    return c;
  }
  return c?.url || c?.link || "";
}

function citationLabel(c) {
  if (typeof c === "string") {
    return c;
  }
  return c?.title || c?.url || c?.link || "";
}

export default function SummaryTab({ candidate, isGeneratingSummary }) {
  const s = candidate?.internal_summary;
  const mutation = useGenerateCandidateSummary(candidate?.uuid);
  const [citationsOpen, setCitationsOpen] = useState(false);
  const loadingToastId = useRef(null);

  const citations = Array.isArray(s?.citations) ? s.citations : [];

  useEffect(
    () => () => {
      if (loadingToastId.current) {
        toast.dismiss(loadingToastId.current);
        loadingToastId.current = null;
      }
    },
    []
  );

  const handleGenerate = () => {
    const toastId = toast.loading(
      s ? "Regenerating summary..." : "Generating summary..."
    );
    loadingToastId.current = toastId;
    mutation.mutate(undefined, {
      onSuccess: () => {
        toast.dismiss(toastId);
        loadingToastId.current = null;
        toast.success("Summary ready");
      },
      onError: (error) => {
        toast.dismiss(toastId);
        loadingToastId.current = null;
        toast.error(error?.message || "Failed to generate summary");
      },
    });
  };

  const skills = Array.isArray(candidate?.skills) ? candidate.skills : [];

  // The auto-summary is generated asynchronously after a CV import completes, so
  // a freshly-imported candidate has no summary yet but one is on the way. Two
  // signals tell us to wait:
  //   - `summary_pending`: an authoritative server flag set when the import
  //     dispatches the generation job and cleared when it finishes/fails. This
  //     survives navigation and reloads, so it covers the common case of opening
  //     the candidate moments after import (the in-flight job is no longer in the
  //     client jobs list, so `isGeneratingSummary` is already false here).
  //   - `isGeneratingSummary`: a best-effort, jobs-list flag that covers watching
  //     the page while the import itself is still running.
  // Guard both with `!s`: once a summary exists we never show the loader, so a
  // stale `summary_pending` can't trap the tab in a spinner. `mutation.isPending`
  // handles a manual Refresh.
  const summaryPending = Boolean(candidate?.summary_pending);
  const isLoadingSummary =
    ((isGeneratingSummary || summaryPending) && !s) || mutation.isPending;

  return (
    <Box>
      {isLoadingSummary ? (
        <SummaryLoading />
      ) : !s ? (
        <EmptyState>
          <Typography variant="body1" color="text.secondary">
            No summary generated yet.
          </Typography>
          <Button
            variant="outlined"
            startIcon={
              mutation.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoAwesomeIcon />
              )
            }
            onClick={handleGenerate}
            disabled={mutation.isPending}
          >
            Generate summary
          </Button>
        </EmptyState>
      ) : (
        <Box>
          <Header>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                textTransform: "uppercase",
                fontSize: "0.7rem",
                letterSpacing: "0.04em",
              }}
            >
              {relativeTime(s.generated_at)}
            </Typography>
            <Button
              size="small"
              startIcon={
                mutation.isPending ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={handleGenerate}
              disabled={mutation.isPending}
            >
              Refresh
            </Button>
          </Header>

          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {s.narrative}
          </Typography>

          {s.pitch_angles?.length > 0 && (
            <Section>
              <SectionTitle>
                Pitch Angles <Count>{s.pitch_angles.length}</Count>
              </SectionTitle>
              <Bullets>
                {s.pitch_angles.map((item, i) => (
                  <li key={i}>
                    <Typography variant="body2">{item}</Typography>
                  </li>
                ))}
              </Bullets>
            </Section>
          )}

          {s.best_fit_role_types?.length > 0 && (
            <Section>
              <SectionTitle>
                Best-fit Role Types <Count>{s.best_fit_role_types.length}</Count>
              </SectionTitle>
              <ChipRow>
                {s.best_fit_role_types.map((role, i) => (
                  <Chip key={i} label={role} size="small" variant="outlined" />
                ))}
              </ChipRow>
            </Section>
          )}

          {s.suggested_companies?.length > 0 && (
            <Section>
              <SectionTitle>
                Suggested Companies <Count>{s.suggested_companies.length}</Count>
              </SectionTitle>
              <Bullets>
                {s.suggested_companies.map((c, i) => (
                  <li key={i}>
                    <Typography variant="body2">
                      <strong>{c.company}</strong>
                      {c.reason ? ` — ${c.reason}` : ""}
                    </Typography>
                  </li>
                ))}
              </Bullets>
            </Section>
          )}

          {s.personalisation?.length > 0 && (
            <Section>
              <SectionTitle>
                Personalisation for Outreach{" "}
                <Count>{s.personalisation.length}</Count>
              </SectionTitle>
              <Bullets>
                {s.personalisation.map((item, i) => (
                  <li key={i}>
                    <Typography variant="body2">{item}</Typography>
                  </li>
                ))}
              </Bullets>
            </Section>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 3 }}
          >
            From public web sources ·{" "}
            {citations.length > 0 ? (
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={() => setCitationsOpen(true)}
              >
                {citations.length} citations
              </Link>
            ) : (
              "0 citations"
            )}
          </Typography>

          <Dialog
            open={citationsOpen}
            onClose={() => setCitationsOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              Sources
              <IconButton
                aria-label="Close"
                size="small"
                onClick={() => setCitationsOpen(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box
                component="ol"
                sx={{
                  m: 0,
                  pl: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {citations.map((c, i) => {
                  const url = citationUrl(c);
                  return (
                    <li key={i}>
                      {url ? (
                        <Link
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          sx={{ wordBreak: "break-all" }}
                        >
                          {citationLabel(c)}
                        </Link>
                      ) : (
                        <Typography variant="body2">
                          {citationLabel(c)}
                        </Typography>
                      )}
                    </li>
                  );
                })}
              </Box>
            </DialogContent>
          </Dialog>
        </Box>
      )}

      <Divider sx={{ my: 4 }} />

      <Typography variant="h2">Skills</Typography>
      {skills.length > 0 ? (
        <ChipRow sx={{ mt: 2 }}>
          {skills.map((skill) => (
            <Chip
              key={skill.id}
              label={skill.name}
              size="small"
              variant="outlined"
            />
          ))}
        </ChipRow>
      ) : (
        <Box sx={{ mt: 2, fontStyle: "italic" }}>No skills</Box>
      )}
    </Box>
  );
}
